import { useEffect, useRef, useState, type RefObject } from "react";
import { formatBRL } from "@/lib/billing-plans";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** true quando o sistema pede menos movimento. Acompanha mudancas ao vivo. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.matchMedia(REDUCED_MOTION_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event: MediaQueryListEvent): void => setReduced(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/** true enquanto o elemento esta visivel E a aba esta em primeiro plano. */
export function useIsActiveOnScreen<T extends HTMLElement>(): { ref: RefObject<T>; active: boolean } {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), {
      threshold: 0.2,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = (): void => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  return { ref, active: inView && pageVisible };
}

const COUNT_DURATION_MS = 1400;

function formatCents(cents: number): string {
  return formatBRL(cents / 100);
}

/**
 * Conta de 0 ate `target` (centavos) escrevendo direto no DOM via rAF, sem
 * re-render por frame. Sem `run` mostra zero; com movimento reduzido, o final.
 */
export function useCountUp(target: number, run: boolean, reduced: boolean): RefObject<HTMLSpanElement> {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!run) {
      node.textContent = formatCents(0);
      return;
    }
    if (reduced) {
      node.textContent = formatCents(target);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const progress = Math.min(1, (now - start) / COUNT_DURATION_MS);
      const eased = 1 - Math.pow(2, -10 * progress);
      node.textContent = formatCents(Math.round(target * (progress === 1 ? 1 : eased)));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, run, reduced]);

  return ref;
}
